import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Eye, EyeOff, Plus, StickyNote } from "lucide-react";
import AddNoteModal from "../Orders/AddNoteModal";
import api from "../../hooks/api";
import { API_URLS } from "../../App";
import Note from "../../model/Note";

interface NotesSectionProps {
  entityId: number;
  entityType: "order" | "offer";
  showVisibilityOption?: boolean;
}

const NotesSection: React.FC<NotesSectionProps> = ({
  entityId,
  entityType,
  showVisibilityOption = true,
}) => {
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch notes from the new API
  useEffect(() => {
    const fetchNotes = async () => {
      if (!entityId) return; // Ensure entityId is valid
      try {
        setLoading(true);
        const params =
          entityType === "offer"
            ? { offerId: entityId }
            : { orderId: entityId };
        const response = await api.get(API_URLS.NOTES, { params });
        setNotes(response.data);
      } catch (error) {
        console.error("Error fetching notes:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchNotes();
  }, [entityId, entityType]);

  const handleAddNote = async (content: string, visibleToReseller: boolean) => {
    try {
      const payload = {
        content,
        visible_to_reseller: visibleToReseller,
        ...(entityType === "offer"
          ? { offer_id: entityId }
          : { order_id: entityId }),
      };
      const response = await api.post(API_URLS.NOTES, payload);
      setNotes([response.data, ...notes]); // Add new note to the top
      setIsAddNoteModalOpen(false);
    } catch (error) {
      console.error("Error adding note:", error);
      alert("Failed to add note. Please try again.");
    }
  };

  const getStatusBadgeClass = (status?: string) => {
    if (!status) return "is-light";
    switch (status.toUpperCase()) {
      case "PENDING":
        return "is-warning";
      case "CONFIRMED":
        return "is-info";
      case "REJECTED":
        return "is-danger";
      case "CANCELLED":
        return "is-dark";
      case "SHIPPED":
        return "is-link";
      case "COMPLETED":
        return "is-success";
      default:
        return "is-light";
    }
  };

  const entityLabel = entityType === "order" ? "Encomenda" : "Proposta";

  return (
    <div className="notes-section">
      <div className="level mb-4">
        <div className="level-left">
          <div className="level-item">
            <h3 className="title is-4">
              <StickyNote className="icon is-small mr-2 inline" />
              Notas da {entityLabel}
            </h3>
          </div>
        </div>
        <div className="level-right">
          <div className="level-item">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="button is-primary"
              type="button"
              onClick={() => setIsAddNoteModalOpen(true)}
            >
              <Plus className="icon is-small mr-1" />
              Adicionar Nota
            </motion.button>
          </div>
        </div>
      </div>

      {notes.length === 0 ? (
        <div className="notification is-light">
          <p className="has-text-centered">
            Nenhuma nota registada para esta {entityLabel.toLowerCase()}.
          </p>
        </div>
      ) : (
        <div className="notes-list">
          {notes.map((note, index) => (
            <div
              key={index}
              className="box mb-3"
              style={{
                borderLeft:
                  note.offer_id && note.order_id
                    ? "4px solid #3273dc"
                    : note.offer_id
                      ? "4px solid #48c774"
                      : "4px solid #ffdd57",
              }}
            >
              <div className="level mb-2">
                <div className="level-left">
                  {note.status && (
                    <div className="level-item">
                      <span
                        className={`tag ${getStatusBadgeClass(note.status)}`}
                      >
                        {note.status}
                      </span>
                    </div>
                  )}
                  {showVisibilityOption && (
                    <div className="level-item">
                      <span className="tag is-light">
                        {note.visible_to_reseller ? (
                          <>
                            <Eye className="icon is-small mr-1" size={14} />
                            Visível ao revendedor
                          </>
                        ) : note.offer_id ? (
                          <>
                            <Eye className="icon is-small mr-1" size={14} />
                            Nota do vendedor
                          </>
                        ) : (
                          <>
                            <EyeOff className="icon is-small mr-1" size={14} />
                            Apenas interno
                          </>
                        )}
                      </span>
                    </div>
                  )}
                </div>
                <div className="level-right">
                  <div className="level-item">
                    <small className="has-text-grey">
                      {new Date(note.created_at).toLocaleString("pt-PT")}
                    </small>
                  </div>
                </div>
              </div>
              <div className="content">
                <p style={{ whiteSpace: "pre-wrap" }}>{note.content}</p>
              </div>
              <div className="has-text-grey is-size-7">
                <strong>Por:</strong> {note.created_by}
              </div>
            </div>
          ))}
        </div>
      )}

      <AddNoteModal
        isOpen={isAddNoteModalOpen}
        onClose={() => setIsAddNoteModalOpen(false)}
        onSubmit={handleAddNote}
        showVisibilityOption={showVisibilityOption}
      />
    </div>
  );
};

export default NotesSection;
